import { useQueryClient } from '@tanstack/react-query'
import { Button, Flex, Modal } from 'antd'
import { ModalProps } from 'antd/lib'
import dayjs from 'dayjs'

import {
  useInvestigationsServiceGetListSpecimensEndpointKey,
  useInvestigationsServiceGetSpecimenEndpoint,
  useInvestigationsServiceUpdateSpecimenEndpoint
} from 'src/api/queries'
import { ApiError, SpecimenLaboratoryStatus } from 'src/api/requests'
import { message, SaveConfirm, Text } from 'src/components'
import { getFullName } from 'src/features/users/utils'
import { dayMonthYearHourMinutesFormatWithDot } from 'src/libs'
import { useLocales } from 'src/locales'
import { formErrorHandler } from 'src/utils'

type Props = {
  id?: string
  onCancel: () => void
} & ModalProps

export const ValidateInvestigationModal = ({
  id,
  onCancel,
  ...props
}: Props) => {
  const { t } = useLocales()

  const queryClient = useQueryClient()

  const { data, isLoading } = useInvestigationsServiceGetSpecimenEndpoint(
    {
      id: String(id)
    },
    [id],
    { enabled: !!id }
  )

  const { mutate, isPending } = useInvestigationsServiceUpdateSpecimenEndpoint({
    onError: (e: ApiError) => formErrorHandler(e.body?.errors),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [useInvestigationsServiceGetListSpecimensEndpointKey]
      })
      message.success(t('investigations.validate_investigation_success'))
      onCancel()
    }
  })

  const onValidate = () => {
    mutate({
      id: String(id),
      requestBody: { laboratoryStatus: SpecimenLaboratoryStatus.VALIDATED }
    })
  }

  return (
    <Modal
      width={520}
      footer={null}
      maskClosable={false}
      title={t('investigations.validate_investigation')}
      onCancel={onCancel}
      loading={isLoading}
      open
      {...props}
    >
      <Flex vertical gap={24}>
        <Flex vertical gap="4px">
          <Text>{t('common.comments')}</Text>
          <Text weight={400}>
            {data?.comments?.length
              ? data?.comments?.map((c) => (
                  <div key={c.id}>
                    {c.text} -{' '}
                    {getFullName(
                      c.createdByUser?.lastName,
                      c.createdByUser?.firstName
                    )}
                    {', '}
                    {c.createdByUser?.institution?.name},{' '}
                    {dayjs(c.createDate).format(
                      dayMonthYearHourMinutesFormatWithDot
                    )}
                  </div>
                ))
              : '-'}
          </Text>
        </Flex>

        <Flex justify="center" gap="16px">
          <Button onClick={onCancel} loading={isPending}>
            {t('common.cancel')}
          </Button>

          <SaveConfirm
            title={`${t('investigations.validate')}?`}
            onConfirm={onValidate}
            okButtonProps={{ loading: isPending }}
          >
            <Button type="primary">{t('investigations.validate')}</Button>
          </SaveConfirm>
        </Flex>
      </Flex>
    </Modal>
  )
}
